import { I18n } from "@assets/strings";
import { debounce } from "lodash";
import { useObservable } from "micro-observables";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import tarificationIcon from "@assets/images/shareroom/tarification-icon.svg";
import verificationIcon from "@assets/images/shareroom/verification-icon.svg";
import { allCurrenciesCode, CurrencyCode } from "@domain/currency/currency";
import { useConversion } from "@domain/currency/hook";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Group } from "@domain/group/group";
import { allSubscriptionMode, SubscriptionMode } from "@domain/group/shareroom/shareroom";
import { currencyFormater } from "@utils/number";
import { PrimaryBold } from "@ui/components/containers/primary";
import { Title } from "@ui/components/containers/title";
import { Selecter } from "@ui/components/inputs/selecter";
import { RoundedTextInput } from "@ui/components/inputs/textInput";
import { Row, Stack, stack } from "@ui/components/layout";
import { desktop, mobile } from "@ui/styles/utils";
import { StepIndicator } from "@ui/pages/group/groupEditorPage/stepIndicator";
import { ShareroomBlocs } from "@ui/pages/group/shareroom/shareroomInfo";
import {
	SettingEditorController,
	settingEditorSteps,
} from "@ui/pages/group/shareroom/dashboard/settingEditorPage/settingEditorController/settingEditorController";
import { SettingEditorControllerContext } from "@ui/pages/group/shareroom/dashboard/settingEditorPage/settingEditorController/settingEditorControllerContext";
import { SettingEditorNavigation } from "./settingEditorNavigation";
import { ShareroomBlocAboutValidator } from "./shareroomAboutBlocValidator";

interface SettingEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	initialShareroom: Group;
}

export const SettingEditor: React.FC<SettingEditorProps> = ({ initialShareroom, ...htmlProps }) => {
	const { pricing } = initialShareroom;
	const currentProfile = useCurrentProfile();
	const controller = useMemo(
		() =>
			new SettingEditorController({
				...initialShareroom,
				//TODO refactor this

				currency: pricing.currency ?? "EUR",
				price: pricing.price ?? 0,
				subscriptionMode: pricing.subscriptionMode,
			}),
		[initialShareroom]
	);
	const step = useObservable(controller.step);
	const { price, currency, subscriptionMode } = useObservable(controller.group);

	const debouncedChange = useCallback(
		debounce(
			(price: string) => {
				setDebouncedPrice(+price);
			},
			500,
			{ leading: true, trailing: true }
		),
		[]
	);

	useEffect(() => () => debouncedChange.cancel(), []);

	const [debouncedPrice, setDebouncedPrice] = useState<number>(price);

	const { result: convertedPrice, loading } = useConversion(currency, debouncedPrice);

	return (
		<SettingEditorControllerContext.Provider value={controller}>
			<Container {...htmlProps}>
				<StepIndicator steps={settingEditorSteps.length} currentStep={settingEditorSteps.indexOf(step)} />

				{step === "CHECK" ? (
					<CheckingBloc>
						<Stack gap={43} align="center">
							<Stack gap={43} align="center">
								<Stack gap={30} align="center">
									<img src={verificationIcon} />
									<Title>{I18n.t("shareroom.dashboard.check.title")}</Title>
								</Stack>
								<StyledDescription>{I18n.t("shareroom.dashboard.check.description")}</StyledDescription>
							</Stack>
							<Stack style={{ width: "100%" }} gap={30}>
								<List>
									{ShareroomBlocs.map((bloc) => (
										<Bloc key={bloc}>
											<li>{I18n.t(`shareroom.about.${bloc}.title`)}</li>
											<ShareroomBlocAboutValidator field={bloc} shareroom={initialShareroom} />
										</Bloc>
									))}
								</List>
							</Stack>
						</Stack>
					</CheckingBloc>
				) : step === "PRICING" ? (
					<PricingBloc>
						<Stack gap={43} align="center">
							<Stack gap={30} align="center">
								<img src={tarificationIcon} />
								<Title>{I18n.t("shareroom.dashboard.pricing.title")}</Title>
							</Stack>
							<StyledDescription>{I18n.t("shareroom.dashboard.pricing.description")}</StyledDescription>
						</Stack>

						<Stack style={{ width: "100%" }} gap={50}>
							<InfoEdition>
								<PrimaryBold>{I18n.t("shareroom.dashboard.pricing.form.subscription_mode.label")}</PrimaryBold>
								<Selecter
									value={subscriptionMode}
									onChange={(value) => controller.setSubscriptionMode(value as SubscriptionMode)}
									options={allSubscriptionMode.map((subscriptionMode) => ({
										label: I18n.t(`shareroom.dashboard.pricing.form.subscription_mode.${subscriptionMode}`),
										value: subscriptionMode,
									}))}
								/>
							</InfoEdition>
							<InfoForm>
								<Stack gap={8}>
									<InfoEdition>
										<PrimaryBold>{I18n.t("shareroom.dashboard.pricing.form.subscription_price")}</PrimaryBold>
										<RoundedTextInput
											value={price?.toString()}
											onChange={(e) => {
												if (!isNaN(+e.currentTarget.value)) {
													controller.setPrice(+e.currentTarget.value);
													debouncedChange(e.currentTarget.value);
												}
											}}
										/>
									</InfoEdition>
									{loading ? <ClipLoader /> : null}
									{convertedPrice ? (
										<Row align="center" justify="center" gap={20}>
											{Object.entries(convertedPrice).map((value) => (
												<Row key={value[0]} gap={0}>
													<Price>
														{currencyFormater(value[1], value[0] as CurrencyCode, currentProfile?.language)}
													</Price>
												</Row>
											))}
										</Row>
									) : null}
								</Stack>
								<InfoEdition>
									<PrimaryBold>{I18n.t("shareroom.dashboard.pricing.form.currency.label")}</PrimaryBold>
									<Selecter
										value={currency}
										onChange={(value) => controller.setCurrency(value as CurrencyCode)}
										options={allCurrenciesCode.map((curreny) => ({
											label: I18n.t(`shareroom.dashboard.pricing.form.currency.${curreny}`),
											value: curreny,
										}))}
									/>
								</InfoEdition>
							</InfoForm>
						</Stack>
					</PricingBloc>
				) : null}
				<StyledSettingEditorNavigation initialShareroom={initialShareroom} />
			</Container>
		</SettingEditorControllerContext.Provider>
	);
};

const Container = styled.div`
	width: 100%;
	${stack(30, "center", "center")}
`;

const CheckingBloc = styled.div`
	width: 100%;
	${stack(20, undefined, "center")}
	@media ${desktop} {
		width: 60%;
	}
`;
const PricingBloc = styled.div`
	width: 100%;
	${stack(43, undefined, "center")}
	@media ${desktop} {
		width: 60%;
	}
`;

const StyledDescription = styled.div`
	${({ theme }) => theme.textStyles.description}
	font-size: 0.938rem !important;
	text-align: left;
`;

const List = styled.ul`
	width: 100%;
	${stack(30, "center")};
	margin-left: 20px;
`;

const Bloc = styled.div`
	width: 100%;
	display: grid;
	grid-template-columns: 1fr 100px;
	align-items: center;
	justify-content: space-between;
`;

const InfoForm = styled.div`
	display: grid;
	width: 100%;
	gap: 20px;
	grid-template-columns: 60% 40%;

	@media ${mobile} {
		grid-template-columns: 100%;
	}
`;

const StyledSettingEditorNavigation = styled(SettingEditorNavigation)`
	margin-top: 50px;
`;

const InfoEdition = styled.div`
	width: 100%;
	${stack(10, "flex-start", "stretch")};
`;

const Price = styled.div`
	font-size: 0.75rem;
	font-weight: 700;
	color: ${({ theme }) => theme.colors.secondary};
`;
